import { Global, css } from '@emotion/react'
import { Link } from 'gatsby'
import { throttle } from 'lodash'
import {
  type ComponentProps,
  Fragment,
  useEffect,
  useRef,
  useState,
} from 'react'
import { TfiMenuAlt } from 'react-icons/tfi'

import { absoluteFill, mq } from '@/theme/mixins'
import { colors } from '@/theme/variables'

import { useClassNotesContext } from '../../context/classNotesContext'

interface Props extends ComponentProps<'nav'> {
  allNotesThisYear?:
    | { heading: string; slug: string; id: string }[]
    | undefined
  initialScrollPos?: number
}

export const ClassNotesList = ({
  allNotesThisYear,
  initialScrollPos,
  ...props
}: Props) => {
  const ref = useRef<HTMLElement | null>(null)
  const [scrollPos, setScrollPos] = useState(initialScrollPos || 0)
  useEffect(() => {
    ref.current?.scrollTo({ top: initialScrollPos })
  }, [initialScrollPos])
  useEffect(() => {
    const container = ref.current
    const handleScroll = throttle(() => {
      setScrollPos(container?.scrollTop || 0)
    }, 50)
    container?.addEventListener('scroll', handleScroll, {
      passive: true,
    })
    return () => {
      container?.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const { isNotesListExpanded, setNotesListExpanded } =
    useClassNotesContext()

  const styles = {
    global: css`
      ${mq().ms} {
        body {
          ${isNotesListExpanded &&
          css`
            overflow: hidden;
          `}
        }
      }
    `,
    nav: css`
      background: ${colors.gray95};
      width: min(35rem, 33vw);
      position: sticky;
      bottom: 0;
      top: calc(var(--nav-height-min) + var(--cnn-height));
      height: calc(100vh - var(--nav-height-min) - var(--cnn-height));
      align-self: flex-start;
      display: grid;
      box-sizing: border-box;
      ${mq().ms} {
        position: fixed;
        top: calc(var(--nav-height-min));
        height: calc(100vh - var(--nav-height-min));
        left: 0;
        width: fit-content;
        z-index: 4;
        --content-width: min(95vw - 3rem, 25rem);
        grid-template-columns: var(--content-width) 3rem;
        transition: grid-template-columns 400ms ease;
        overflow: hidden;
        ${!isNotesListExpanded &&
        css`
          grid-template-columns: 0vw 3rem;
        `}
      }
    `,
    inner: css`
      position: absolute;
      overflow: auto;
      min-height: 100%;
      width: 100%;
      min-width: var(--content-width);
      grid-column: 1 / 2;
    `,
    innerInner: css`
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      display: grid;
      box-sizing: border-box;
      align-content: flex-start;
      height: fit-content;
      padding-bottom: 1rem;
    `,
    link: css`
      position: relative;
      padding: 0.875em var(--gtr-36) 0.875em var(--margin);
      font-size: var(--fs-24);
      font-family: var(--ff-display-optical);
      text-decoration: none;
      color: ${colors.gray20};
      margin-bottom: -1px;
      transition: all 300ms ease;
      ${mq().ms} {
        padding-right: var(--margin);
      }
      &:before {
        content: '';
        display: block;
        position: absolute;
        width: calc(100% - 2 * var(--gtr-36));
        height: 1px;
        background: #00000033;
        left: var(--gtr-36);
        bottom: 0;
        transition: all 300ms ease;
      }
      &[aria-current] {
        background: ${colors.gray90};
        &:hover {
          background: ${colors.gray90};
        }
        &:before {
          background: transparent;
        }
      }
      @media (hover: hover) {
        &:hover {
          color: ${colors.red};
          background: ${colors.gray93};
          &:before {
            background: transparent;
          }
        }
      }
    `,
    overlay: css`
      display: none;
      ${mq().ms} {
        display: block;
        ${absoluteFill};
        position: fixed;
        background-color: #000000ee;
        z-index: 3;
        transition: background-color 300ms ease;
        ${!isNotesListExpanded &&
        css`
          background-color: transparent;
          pointer-events: none;
        `}
      }
    `,
    trayButton: css`
      display: none;

      ${mq().ms} {
        grid-column: 2 / 3;
        display: block;
        position: relative;
        background-color: ${colors.gray85};
        color: #00000044;
        > span {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(90deg);
          text-transform: uppercase;
          font-size: var(--fs-15);
          letter-spacing: 0.05em;
          font-weight: 500;
          width: max-content;
          transition: opacity 300ms ease;
          &:first-of-type {
            opacity: ${isNotesListExpanded ? 0 : 1};
          }
          &:last-of-type {
            opacity: ${isNotesListExpanded ? 1 : 0};
          }
        }
      }
    `,
    menuIcon: css`
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: var(--cnn-height);
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: ${colors.gray30};
      color: #fff;
      font-size: var(--fs-24);
    `,
  }
  return (
    <Fragment>
      <nav
        css={styles.nav}
        ref={ref}
        {...props}
      >
        <Global styles={styles.global} />
        <div css={styles.inner}>
          <div css={styles.innerInner}>
            {allNotesThisYear?.map(note => (
              <Link
                css={styles.link}
                to={`/${note.slug}/`}
                key={note.id}
                state={{ listScrollPos: scrollPos }}
                onClick={() => {
                  setTimeout(() => {
                    setNotesListExpanded(false)
                  }, 10)
                }}
              >
                {note.heading}
              </Link>
            ))}
          </div>
        </div>
        <button
          css={styles.trayButton}
          onClick={() => setNotesListExpanded(prev => !prev)}
        >
          <div css={styles.menuIcon}>
            <TfiMenuAlt />
          </div>
          <span>Browse Notes</span>
          <span>Collapse</span>
        </button>
      </nav>
      <div css={styles.overlay} />
    </Fragment>
  )
}
